import { EditFilled } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
} from "antd";
import React, { useCallback, useEffect } from "react";

const CommercialPackingRequirementForm = React.memo(
  ({ mode, data, setData }) => {
    const [form] = Form.useForm();
    const [open, setOpen] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState(null);
    const [selectedRowIndex, setSelectedRowIndex] = React.useState(null);

    // form watch fields
    const commercialLength = Form.useWatch("commercial_length", form);
    const commercialWidth = Form.useWatch("commercial_width", form);
    const commercialHeight = Form.useWatch("commercial_height", form);

    const onFinish = useCallback(
      (values) => {
        let cartons = [...data?.cartons];
        cartons[selectedRowIndex] = {
          ...cartons[selectedRowIndex],
          ...values,
        };
        setData({ ...data, cartons });
        setOpen(false);
        setSelectedRow(null);
        setSelectedRowIndex(null);
      },
      [data, setData, selectedRowIndex],
    );

    const onChange = (name, value) => {
      setData({
        ...data,
        [name]: value,
      });
    };

    useEffect(() => {
      // calculate commercial cbm
      if (commercialLength && commercialWidth && commercialHeight) {
        const cbm =
          (commercialLength * commercialWidth * commercialHeight) / 1000000;
        form.setFieldsValue({ commercial_cbm: Number(cbm).toFixed(2) });
      }
    }, [commercialLength, commercialWidth, commercialHeight, form]);

    return (
      <Card title="Commercial Packing Requirement Form" style={{ margin: 16 }}>
        <Row gutter={24}>
          <Col span={8} style={{ marginTop: 10 }}>
            <span style={{ fontWeight: "normal", marginTop: 5 }}>
              Container Number:
            </span>
            <Input
              onChange={(e) => onChange("container_number", e.target.value)}
              value={data?.container_number}
              disabled={mode === "view"}
            />
          </Col>
          <Col span={8} style={{ marginTop: 10 }}>
            <span style={{ fontWeight: "normal", marginTop: 5 }}>
              Seal Number:
            </span>
            <Input
              onChange={(e) => onChange("seal_number", e.target.value)}
              value={data?.seal_number}
              disabled={mode === "view"}
            />
          </Col>
          <Col span={8} style={{ marginTop: 10 }}>
            <span style={{ fontWeight: "normal", marginTop: 5 }}>
              Feeder Vessel:
            </span>
            <Input
              onChange={(e) => onChange("fedder_vessel", e.target.value)}
              value={data?.fedder_vessel}
              disabled={mode === "view"}
            />
          </Col>
          <Col span={8} style={{ marginTop: 10 }}>
            <span style={{ fontWeight: "normal", marginTop: 5 }}>
              Mother Vessel:
            </span>
            <Input
              onChange={(e) => onChange("mother_vessel", e.target.value)}
              value={data?.mother_vessel}
              disabled={mode === "view"}
            />
          </Col>
          <Col span={8} style={{ marginTop: 10 }}>
            <span style={{ fontWeight: "normal", marginTop: 5 }}>ETA:</span>
            <Input
              onChange={(e) => onChange("eta", e.target.value)}
              value={data?.eta}
              disabled={mode === "view"}
            />
          </Col>
          <Col span={8} style={{ marginTop: 10 }}>
            <span style={{ fontWeight: "normal", marginTop: 5 }}>ETD:</span>
            <Input
              onChange={(e) => onChange("etd", e.target.value)}
              value={data?.etd}
              disabled={mode === "view"}
            />
          </Col>
        </Row>

        <Card
          title="CARTON DETAILS"
          style={{ marginTop: 16 }}
          headStyle={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}
        >
          <>
            <div className="custome-table">
              <table style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th colSpan={16} className="bc-orange text-center">
                      Carton Details
                    </th>
                    <th colSpan={4} className="bc-orange text-center">
                      Carton Size
                    </th>
                    <th colSpan={2} className="bc-orange text-center">
                      Weight (kg) / Carton
                    </th>
                    {mode !== "view" && (
                      <th className="bc-orange text-center">Action</th>
                    )}
                  </tr>
                  <tr>
                    <th className="bc-java text-center">SL</th>
                    <th className="bc-java text-center">From *</th>
                    <th className="bc-java text-center">To *</th>
                    <th className="bc-java text-center">Qty *</th>
                    <th className="bc-java text-center">Customer PO</th>
                    <th className="bc-java text-center">Assortment</th>
                    <th className="bc-java text-center">Item</th>
                    <th className="bc-java text-center">UPC</th>
                    <th className="bc-java text-center">Length</th>
                    <th className="bc-java text-center">Width</th>
                    <th className="bc-java text-center">Height</th>
                    <th className="bc-java text-center">CBM</th>
                    <th className="bc-java text-center">Gross</th>
                    <th className="bc-java text-center">Net</th>
                    <th className="bc-java text-center">Total Gross</th>
                    <th className="bc-java text-center">Total Net</th>

                    <th className="bc-java text-center">L (cm) *</th>
                    <th className="bc-java text-center">W (cm) *</th>
                    <th className="bc-java text-center">H (cm) *</th>
                    <th className="bc-java text-center">CBM *</th>
                    <th className="bc-java text-center">Gross *</th>
                    <th className="bc-java text-center">Net *</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.cartons?.map((carton, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{carton.start_range}</td>
                      <td>{carton.end_range}</td>
                      <td>{carton.carton_qty}</td>
                      <td>{carton.customer_po}</td>
                      <td>{carton.assortment}</td>
                      <td>{carton.item}</td>
                      <td>{carton.upc}</td>
                      <td>{carton.length}</td>
                      <td>{carton.width}</td>
                      <td>{carton.height}</td>
                      <td>{carton.cbm}</td>
                      <td>{carton.gross}</td>
                      <td>{carton.net}</td>
                      <td>{carton.total_gross}</td>
                      <td>{carton.total_net}</td>
                      <td>{carton.commercial_length}</td>
                      <td>{carton.commercial_width}</td>
                      <td>{carton.commercial_height}</td>
                      <td>{carton.commercial_cbm}</td>

                      <td>{carton.commercial_gross_weight}</td>
                      <td>{carton.commercial_net_weight}</td>

                      {mode !== "view" && (
                        <td>
                          <Button
                            type="default"
                            icon={<EditFilled />}
                            onClick={() => {
                              form.setFieldsValue({
                                commercial_length: carton.commercial_length,
                                commercial_width: carton.commercial_width,
                                commercial_height: carton.commercial_height,
                                commercial_cbm: carton.commercial_cbm,
                                commercial_gross_weight:
                                  carton.commercial_gross_weight,
                                commercial_net_weight:
                                  carton.commercial_net_weight,
                              });

                              setOpen(true);
                              setSelectedRow(carton);
                              setSelectedRowIndex(index);
                            }}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
          <Drawer
            title="Edit Carton"
            placement="right"
            onClose={() => setOpen(false)}
            open={open}
            width={400}
            closable={false}
            destroyOnClose
            maskClosable={false}
            extra={
              <Space>
                <Button
                  onClick={() => {
                    setOpen(false);
                    setSelectedRow(null);
                    setSelectedRowIndex(null);
                  }}
                  type="default"
                  danger
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    form.submit();
                  }}
                  type="primary"
                >
                  Submit
                </Button>
              </Space>
            }
          >
            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Row gutter={24}>
                <Col span={24} style={{ marginTop: 10 }}>
                  <Form.Item
                    name="commercial_length"
                    style={{ marginBottom: 0 }}
                    label="Length"
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="Length"
                      min={0}
                      step={0.01}
                      precision={2}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value.replace(/(,*)/g, "")}
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col span={24} style={{ marginTop: 10 }}>
                  <Form.Item
                    name="commercial_width"
                    style={{ marginBottom: 0 }}
                    label="Width"
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="Width"
                      min={0}
                      step={0.01}
                      precision={2}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value.replace(/(,*)/g, "")}
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col span={24} style={{ marginTop: 10 }}>
                  <Form.Item
                    name="commercial_height"
                    style={{ marginBottom: 0 }}
                    label="Height"
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="Height"
                      min={0}
                      step={0.01}
                      precision={2}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value.replace(/(,*)/g, "")}
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col span={24} style={{ marginTop: 10 }}>
                  <Form.Item
                    name="commercial_cbm"
                    style={{ marginBottom: 0 }}
                    label="CBM"
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="CBM"
                      min={0}
                      step={0.01}
                      precision={2}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value.replace(/(,*)/g, "")}
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col span={24} style={{ marginTop: 10 }}>
                  <Form.Item
                    name="commercial_gross_weight"
                    style={{ marginBottom: 0 }}
                    label="Gross Weight"
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="Gross Weight"
                      min={0}
                      step={0.01}
                      precision={2}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value.replace(/(,*)/g, "")}
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col span={24} style={{ marginTop: 10 }}>
                  <Form.Item
                    name="commercial_net_weight"
                    style={{ marginBottom: 0 }}
                    label="Net Weight"
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="Net Weight"
                      min={0}
                      step={0.01}
                      precision={2}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value.replace(/(,*)/g, "")}
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Drawer>
        </Card>
      </Card>
    );
  },
);

export default CommercialPackingRequirementForm;
